// Svelte effects and watchSettled observe settled reactive state and can batch
// multiple writes into one run. This notifier is for write-boundary domain transitions
// that must synchronously deliver every explicit current/previous state pair.
export type StateTransition<T> = {
    current: T
    previous: T
}

export type StateTransitionSubscriber<T> = (transition: StateTransition<T>) => void

export class StateTransitionNotifier<T> {
    readonly #getCurrent: () => T
    readonly #listeners = new Set<StateTransitionSubscriber<T>>()

    public constructor(getCurrent: () => T) {
        this.#getCurrent = getCurrent
    }

    public subscribe = (listener: StateTransitionSubscriber<T>): (() => void) => {
        this.#listeners.add(listener)

        const current = this.#getCurrent()
        listener({
            current,
            previous: current
        })

        return () => {
            this.#listeners.delete(listener)
        }
    }

    public notify(current: T, previous: T): void {
        for (const listener of this.#listeners) {
            listener({
                current,
                previous
            })
        }
    }

    public clear(): void {
        this.#listeners.clear()
    }
}
